import React from 'react';
import { useTranslation } from 'react-i18next';
import CustomTable from '../../../../../../../components/table/CustomTable';
import CustomTh from '../../../../../../../components/table/CustomTh';
import CustomTd from '../../../../../../../components/table/CustomTd';
import {currencyFormat, formatTimeUnit, getDateFormat} from '../../../../../../../Apis/functions';
import TableFunction from '../../../../../../../components/table/TableFunction';
import BreakdownHeaderCell from './BreakdownHeaderCell';
import ScheduledShiftCell from './ScheduledShiftCell';
import {
    getCalculationTypeById, POLICY_CONDITION_MODEL
} from "../../../OvertimePolicies/Global/CreateUpdatePolicy/Model/Model";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * Additive breakdown table: each entry is paid over the same previous rate, so
 * only previous rate, covered hours and the resulting operation are shown.
 * @param {Object} item - Overtime line snapshot.
 */
const HourBreakdownTable = ({ item }) => {
    const { t } = useTranslation();
    const tableId = 'tbl_obd_additive_breakdown';
    const rows = item?.items ?? [];
    const hourlyRate = `$${currencyFormat(item?.hourlyRate)}`;
    const type = getCalculationTypeById(item?.policyCalculationTypeSnapshot);
    const calculationValue = item?.policyCalculationValueSnapshot;
    const rateDeltaTitle = POLICY_CONDITION_MODEL[item?.policyConditionSnapshot]?.getFormula({
        type,
        calculationValue
    })?.rateDeltaTitle;


    const getThead = () => [
        <BreakdownHeaderCell key='scheduled_shift' tableId={tableId} columnKey='scheduled_shift' customWidth={220}/>,
        <BreakdownHeaderCell key='entry_date' tableId={tableId} columnKey='entry_date' customWidth={150}/>,
        <BreakdownHeaderCell key='exit_date' tableId={tableId} columnKey='exit_date' customWidth={150}/>,
        <BreakdownHeaderCell key='hourly_rate' tableId={tableId} columnKey='hourly_rate' customWidth={150} />,
        <BreakdownHeaderCell key='covered_hours' tableId={tableId} columnKey='covered_hours' />,
        <BreakdownHeaderCell key='rate_delta' tableId={tableId} columnKey='rate_delta'
                             value={rateDeltaTitle} />,
        <BreakdownHeaderCell key='operation' tableId={tableId} columnKey='operation' customWidth={200} classes={'text-end'}
            value={`${t('covered_hours')} x ${t('rate_delta')}`} />,
        <CustomTh key='equals' tableId={tableId} classes={'breakdown-fixed-equals'} />,
        <CustomTh key='currency' tableId={tableId} classes={'breakdown-fixed-currency'} />,
        <BreakdownHeaderCell key='result' tableId={tableId} columnKey='result' classes={'text-end breakdown-fixed-col'}/>,
    ];

    const getTbody = () => ({
        data: rows.map((_, i) => ({ id: i })),
        body: [
            ...rows.map((e) => {
                const result = `${currencyFormat(e.result)}`;
                const effectiveRate = `$${currencyFormat(e.effectiveRate)}`;
                const rateDelta = `$${currencyFormat(e.rateDelta)}`;
                const totalOvertimeMinutes = `${formatTimeUnit(e.totalOvertimeMinutes)}`;
                const operationFormula = POLICY_CONDITION_MODEL[item?.policyConditionSnapshot]?.getFormula({
                    type,
                    calculationValue,
                    totalOvertimeMinutes,
                    effectiveRate,
                    rateDelta
                })?.operationFormula;

                return [
                    <CustomTd key='scheduled_shift' tableId={tableId}>
                        <ScheduledShiftCell startDate={e?.scheduledStartDate} endDate={e?.scheduledEndDate} />
                    </CustomTd>,
                    <CustomTd key='entry_date' tableId={tableId}>{getDateFormat(e?.entryDate, true)}</CustomTd>,
                    <CustomTd key='exit_date' tableId={tableId}>{getDateFormat(e?.exitDate, true)}</CustomTd>,
                    <CustomTd key='hourly_rate' tableId={tableId}>{hourlyRate}</CustomTd>,
                    <CustomTd key='covered_hours' tableId={tableId}>{totalOvertimeMinutes}</CustomTd>,
                    <CustomTd key='rate_delta' tableId={tableId}>{rateDelta}</CustomTd>,
                    <CustomTd key='operation' tableId={tableId} classes='text-end'>
                        {operationFormula}
                    </CustomTd>,
                    <CustomTd key='equals' tableId={tableId} classes='text-center breakdown-fixed-equals'>=</CustomTd>,
                    <CustomTd key='currency' tableId={tableId} classes='text-center breakdown-fixed-currency'>$</CustomTd>,
                    <CustomTd key='result' tableId={tableId} classes='text-end breakdown-fixed-col'>{result}</CustomTd>,
                ];
            }),
        ],
    });

    const { thead, tbody } = TableFunction({ getThead, getTbody, data: { content: rows }, dependencies: [rows] });

    return (
        <div className='overflow-x-auto custom-scrollbar'>
            <CustomTable
                tableId={tableId}
                thead={thead}
                tbody={tbody}
                columnActions={false}
                inModal={true}
                withoutHeight={true}
            />
        </div>
    );
};

export default HourBreakdownTable;
